<script setup lang="ts">
/**
 * Aviso que OCUPA O LUGAR da loja para quem está no nível 0.
 *
 * A loja só abre no Bronze 1 — e "Bronze 1" é o NÍVEL 1 da régua da Smartico.
 * O nível 0 é o degrau "Sem elo", onde toda conta nasce (ver `unrankedStep` em
 * utils/levels.ts): ele entra no swiper de elos antes do Bronze, mas não é um
 * elo. Por isso a frase fala do nível 1 como a CONQUISTA do Bronze, e não como
 * "o próximo nível do Bronze".
 *
 * Quem decide exibir é a StoreV2 — este arquivo é burro de propósito (só a
 * copy, a arte e o CTA), então sobe sozinho num harness.
 *
 * As cores e o emblema vêm de `LEVEL_TIERS` em vez de hex solto aqui: é a mesma
 * tabela que pinta a trilha de níveis, então o Bronze do aviso e o Bronze da
 * vitrine não podem divergir.
 */
import { LEVEL_TIERS } from '@/utils/levels';
import { getAssetsUrlForCDN, localeLink } from '@/utils/template';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';

const props = defineProps<{
  // `true` = a loja está montada DENTRO do modal do Clube Fidelidade. Aí o CTA
  // não navega (a rota por baixo é de outro dono): abre a `LevelsSheet`, que
  // só existe montada lá dentro. Na rota /vip/store fica `false` e o CTA leva
  // para /vip/levels.
  embedded?: boolean;
}>();

const bronze = LEVEL_TIERS.find((tier) => tier.key === 'bronze');

// Fallbacks com os valores da tabela: se um dia a chave 'bronze' mudar, o aviso
// continua desenhado (só perde o vínculo) em vez de renderizar sem cor.
const badge = computed(() =>
  getAssetsUrlForCDN(
    bronze?.badge ?? '/assets/images/levels/badges/bronze.webp',
  ),
);

const color = computed(() => bronze?.color ?? '#FF6A00');
const accent = computed(() => bronze?.accent ?? '#FFC324');

const router = useRouter();

function seeLevels() {
  if (props.embedded) {
    useLoyaltyHubStore().openLevels();

    return;
  }

  router.push(localeLink('/vip/levels'));
}
</script>

<template>
  <section
    class="sln"
    role="note"
    :style="{ '--sln-color': color, '--sln-accent': accent }"
  >
    <img
      alt=""
      aria-hidden="true"
      class="sln__badge"
      decoding="async"
      :src="badge"
    />

    <h2 class="sln__title">Sua loja abre no Bronze&nbsp;1</h2>

    <p class="sln__text">
      Os itens da loja ficam disponíveis a partir do <strong>Bronze 1</strong>.
      Você está no nível 0 — alcance o <strong>nível 1</strong> para conquistar
      o Bronze e liberar sua loja.
    </p>

    <button
      class="sln__cta"
      type="button"
      @click="seeLevels"
    >
      Ver meus níveis
    </button>
  </section>
</template>

<style scoped lang="scss">
// Animação por @keyframes, nunca `transition`: o reset global do main.scss mata
// `transition` no app inteiro.
@keyframes sln-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.sln {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 28px 20px 24px;
  text-align: center;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  // Fallback antes do gradiente: se o navegador não conhecer `color-mix`, ele
  // descarta a declaração INTEIRA e o painel ficaria transparente.
  background: #010612;
  // Brilho do elo saindo de cima, sobre o fundo escuro da loja.
  background:
    radial-gradient(
      120% 80% at 50% 0%,
      color-mix(in srgb, var(--sln-color) 18%, transparent) 0%,
      transparent 70%
    ),
    #010612;
  animation: sln-in 260ms ease-out both;
}

.sln__badge {
  width: 84px;
  height: auto;
  user-select: none;
  -webkit-user-drag: none;
}

.sln__title {
  margin: 0;
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.sln__text {
  max-width: 420px;
  margin: 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;

  strong {
    color: var(--sln-accent);
    font-weight: 700;
  }
}

.sln__cta {
  margin-top: 6px;
  padding: 10px 22px;
  border: 0;
  border-radius: 10px;
  background: linear-gradient(
    92deg,
    var(--sln-color) 0%,
    var(--sln-accent) 100%
  );
  color: #1a0f00;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
}

@media (min-width: 768px) {
  .sln {
    padding: 40px 24px 36px;
  }

  .sln__badge {
    width: 104px;
  }

  .sln__title {
    font-size: 22px;
  }

  .sln__text {
    font-size: 14px;
  }
}
</style>
